import React, { useMemo, useCallback } from 'react';
import { Card, Descriptions, Tag, Typography, Space, Divider, Button } from 'antd';
import { CarOutlined, RocketOutlined, SendOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type {
  RouteViewModelOperation,
  RouteViewModelParties,
  RouteViewModelStop
} from '../../../types/routeViewModel';
import { ExecutionCell, StatusCell } from '../../../ui';
import { TransportType } from '../../../types';
import { executorTypeLabel } from '../../../utils/enumUiLabels';
import { navigateToReferenceCard } from '../../../utils/referenceCardNavigation';
import { stopSourceLabel, stopFullAddress } from './RouteStructureBlock';
import { isSequentialStopsReadModeEnabled } from '../../../features/sequentialStops/sequentialStopsReadFlags';
import {
  logSequentialStopsReadShadow,
  resolveStopsReadOrder,
  sortStopsByPosition
} from '../../../features/sequentialStops/sequentialStopsOrdering';
import { formatResponsibleUserLabel } from '../../../utils/userDisplayName';

const { Text } = Typography;

const transportLabels: Record<string, string> = {
  [TransportType.AUTO]: 'Автомобильный',
  [TransportType.RAILWAY]: 'Железнодорожный',
  [TransportType.AVIATION]: 'Авиационный'
};

const transportIcons: Record<string, React.ReactNode> = {
  [TransportType.AUTO]: <CarOutlined />,
  [TransportType.RAILWAY]: <RocketOutlined />,
  [TransportType.AVIATION]: <SendOutlined />
};

function carrierName(p: RouteViewModelParties): string {
  const c = p.carrier;
  if (!c) return '—';
  const n = c.name;
  return typeof n === 'string' && n.trim() ? n : '—';
}

function driverName(p: RouteViewModelParties): string {
  const d = p.driver;
  if (!d) return '—';
  const n = d.fullName;
  return typeof n === 'string' && n.trim() ? n : '—';
}

function vehicleLabel(v: Record<string, unknown> | null): string {
  if (!v) return '—';
  const brand = v.brand != null ? String(v.brand).trim() : '';
  const num = v.registrationNumber != null ? String(v.registrationNumber).trim() : '';
  const name = v.name != null ? String(v.name).trim() : '';
  const parts = [name || brand, num].filter(Boolean);
  const s = parts.join(' · ');
  return s || '—';
}

function partyName(entity: unknown): string | null {
  if (!entity || typeof entity !== 'object') return null;
  const n = (entity as { name?: unknown }).name;
  if (typeof n === 'string' && n.trim()) return n.trim();
  return null;
}

function entityId(e: unknown): number | null {
  if (e == null || typeof e !== 'object') return null;
  const id = (e as { id?: unknown }).id;
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

export interface OperationBlockProps {
  model: RouteViewModelOperation;
  parties: RouteViewModelParties;
  applications: Record<string, unknown>[];
  stops: RouteViewModelStop[];
  adminReopenButton?: React.ReactNode;
}

const OperationBlock: React.FC<OperationBlockProps> = ({
  model,
  parties,
  applications,
  stops,
  adminReopenButton
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const openEntityCard = useCallback(
    (path: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      navigateToReferenceCard(navigate, path, location);
    },
    [navigate, location]
  );

  const status = model.status;
  const transportType = model.transportType;
  const label = transportLabels[transportType] ?? '—';
  const icon = transportIcons[transportType];

  const sequentialRead = isSequentialStopsReadModeEnabled();

  const itineraryStops = useMemo(() => {
    const ordered = sortStopsByPosition(stops as RouteViewModelStop[]);
    if (sequentialRead) {
      logSequentialStopsReadShadow({ context: 'OperationBlock', stops: ordered });
    }
    return ordered;
  }, [stops, sequentialRead]);

  const loadingStops = useMemo(
    () =>
      resolveStopsReadOrder(stops as RouteViewModelStop[]).filter(
        (s) => String(s.type || '').toLowerCase() === 'loading'
      ) as RouteViewModelStop[],
    [stops]
  );

  const unloadingStops = useMemo(
    () =>
      resolveStopsReadOrder(stops as RouteViewModelStop[]).filter(
        (s) => String(s.type || '').toLowerCase() === 'unloading'
      ) as RouteViewModelStop[],
    [stops]
  );

  const lw = model.loadingWarehouse;
  const uw = model.unloadingWarehouse;

  const firstApp = applications[0]?.application as Record<string, unknown> | undefined;

  const clientId = useMemo(() => {
    const fromParty = entityId(parties.client);
    if (fromParty != null) return fromParty;
    const c = firstApp?.client;
    return entityId(c);
  }, [parties.client, firstApp]);

  const customerName = useMemo(() => {
    const app = applications[0]?.application as Record<string, unknown> | undefined;
    return partyName(app?.client) ?? partyName(parties.client) ?? '—';
  }, [applications, parties.client]);

  const shipperName =
    partyName(parties.shipper) ?? partyName(firstApp?.shipper) ?? '—';
  const consigneeName =
    partyName(parties.consignee) ?? partyName(firstApp?.consignee) ?? '—';

  const operationSummaryLine = useMemo(() => {
    const dir = model.direction.label?.trim() || '—';
    const d1 = model.dates.plannedLoading ? dayjs(model.dates.plannedLoading).format('DD.MM') : '';
    const d2 = model.dates.plannedUnloading ? dayjs(model.dates.plannedUnloading).format('DD.MM') : '';
    const dateRange = d1 && d2 ? `${d1}–${d2}` : d1 || d2 || '—';
    return `${dir} • ${dateRange} • ${customerName}`;
  }, [
    model.direction.label,
    model.dates.plannedLoading,
    model.dates.plannedUnloading,
    customerName
  ]);

  return (
    <Card size="small" title="Операция" extra={adminReopenButton} style={{ marginBottom: 16 }}>
      <Text style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>{operationSummaryLine}</Text>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="№ маршрута">
          <Text strong>#{model.id}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Статус">
          <StatusCell status={String(status ?? '')} />
        </Descriptions.Item>
        <Descriptions.Item label="Тип транспорта">
          <Space>
            {icon}
            <Tag color="blue">{label}</Tag>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Исполнитель">{executorTypeLabel(model.executor)}</Descriptions.Item>
        <Descriptions.Item label="Исполнение">
          <ExecutionCell
            route={{
              isCompanyRoute: model.isCompanyRoute,
              carrierContractRequestNumber: model.carrierContractRequest?.number,
              carrierContractRequestDate: model.carrierContractRequest?.date,
              carrierAgreement: null
            }}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Перевозчик">
          {entityId(parties.carrier) != null && carrierName(parties) !== '—' ? (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/carriers/${entityId(parties.carrier)}`)}
              style={{ fontSize: 12, height: 'auto', padding: 0 }}
            >
              {carrierName(parties)}
            </Button>
          ) : (
            carrierName(parties)
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Грузоотправитель">{shipperName}</Descriptions.Item>
        <Descriptions.Item label="Грузополучатель">{consigneeName}</Descriptions.Item>
        <Descriptions.Item label="Водитель">
          {entityId(parties.driver) != null && driverName(parties) !== '—' ? (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/drivers/${entityId(parties.driver)}`)}
              style={{ fontSize: 12, height: 'auto', padding: 0 }}
            >
              {driverName(parties)}
            </Button>
          ) : (
            driverName(parties)
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Тягач">
          {entityId(parties.vehicle) != null && vehicleLabel(parties.vehicle) !== '—' ? (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/vehicles/${entityId(parties.vehicle)}`)}
              style={{ fontSize: 12, height: 'auto', padding: 0 }}
            >
              {vehicleLabel(parties.vehicle)}
            </Button>
          ) : (
            vehicleLabel(parties.vehicle)
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Прицеп">
          {entityId(parties.trailerVehicle) != null && vehicleLabel(parties.trailerVehicle ?? null) !== '—' ? (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/vehicles/${entityId(parties.trailerVehicle)}`)}
              style={{ fontSize: 12, height: 'auto', padding: 0 }}
            >
              {vehicleLabel(parties.trailerVehicle ?? null)}
            </Button>
          ) : (
            vehicleLabel(parties.trailerVehicle ?? null)
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Заказчик">
          {clientId != null && customerName !== '—' ? (
            <Button
              type="link"
              className="routes-entity-link"
              size="small"
              onClick={openEntityCard(`/clients/${clientId}`)}
              style={{ fontSize: 12, height: 'auto', padding: 0 }}
            >
              {customerName}
            </Button>
          ) : (
            customerName
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Ответственный логист">
          {formatResponsibleUserLabel(model.responsibleLogist)}
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Даты (план / факт):</Text>
        <div style={{ fontSize: 12, marginTop: 2 }}>
          <div>
            {model.dates.plannedLoading ? dayjs(model.dates.plannedLoading).format('DD.MM') : '—'} →{' '}
            {model.dates.plannedUnloading ? dayjs(model.dates.plannedUnloading).format('DD.MM') : '—'}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {model.dates.actualLoading ? dayjs(model.dates.actualLoading).format('DD.MM') : '—'} →{' '}
            {model.dates.actualUnloading ? dayjs(model.dates.actualUnloading).format('DD.MM') : '—'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Направление:</Text>
        <div style={{ marginTop: 2 }}>
          <Text strong>{model.direction.label}</Text>
        </div>
      </div>

      {(model.direction.rail.departureStation || model.direction.rail.arrivalStation) && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <Text type="secondary">ЖД:</Text>{' '}
          {model.direction.rail.departureStation || '—'} → {model.direction.rail.arrivalStation || '—'}
          {model.direction.rail.wagonNumber ? ` · вагон ${model.direction.rail.wagonNumber}` : ''}
        </div>
      )}
      {(model.direction.air.departureAirport || model.direction.air.arrivalAirport) && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <Text type="secondary">Авиа:</Text>{' '}
          {model.direction.air.departureAirport || '—'} → {model.direction.air.arrivalAirport || '—'}
          {model.direction.air.flightNumber ? ` · рейс ${model.direction.air.flightNumber}` : ''}
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {sequentialRead ? (
        <>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            Маршрут (порядок исполнения)
          </Text>
          {itineraryStops.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>Нет точек маршрута</Text>
          ) : (
            itineraryStops.map((stop, idx) => {
              const isLoading = String(stop.type || '').toLowerCase() === 'loading';
              const name = stopSourceLabel(stop, applications, lw, uw);
              const city =
                stop.city != null && String(stop.city).trim() ? String(stop.city).trim() : '—';
              const fullAddress = stopFullAddress(
                stop,
                applications,
                lw,
                uw,
                isLoading ? 'loading' : 'unloading'
              );
              return (
                <div
                  key={String(stop.id ?? idx)}
                  style={{ fontSize: 12, marginBottom: idx < itineraryStops.length - 1 ? 12 : 0 }}
                >
                  <Text type="secondary" style={{ display: 'block' }}>
                    {idx + 1}. {isLoading ? 'Погрузка' : 'Выгрузка'}
                    {stop.position != null ? ` (pos ${stop.position})` : ''}
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Название: </Text>
                    <Text>{name}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Город: </Text>
                    <Text>{city}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Адрес: </Text>
                    <Text>{fullAddress}</Text>
                  </div>
                </div>
              );
            })
          )}
        </>
      ) : (
        <>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Погрузка</Text>
          {loadingStops.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>Нет точек погрузки</Text>
          ) : (
            loadingStops.map((stop, idx) => {
              const name = stopSourceLabel(stop, applications, lw, uw);
              const city =
                stop.city != null && String(stop.city).trim() ? String(stop.city).trim() : '—';
              const fullAddress = stopFullAddress(stop, applications, lw, uw, 'loading');
              return (
                <div
                  key={String(stop.id ?? idx)}
                  style={{ fontSize: 12, marginBottom: idx < loadingStops.length - 1 ? 12 : 0 }}
                >
                  {loadingStops.length > 1 ? (
                    <Text type="secondary" style={{ display: 'block' }}>Точка {idx + 1}</Text>
                  ) : null}
                  <div style={{ marginTop: loadingStops.length > 1 ? 4 : 0 }}>
                    <Text type="secondary">Название: </Text>
                    <Text>{name}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Город: </Text>
                    <Text>{city}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Адрес: </Text>
                    <Text>{fullAddress}</Text>
                  </div>
                </div>
              );
            })
          )}

          <Divider style={{ margin: '12px 0' }} />

          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Выгрузка</Text>
          {unloadingStops.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
          ) : (
            unloadingStops.map((stop, idx) => {
              const name = stopSourceLabel(stop, applications, lw, uw);
              const city =
                stop.city != null && String(stop.city).trim() ? String(stop.city).trim() : '—';
              const fullAddress = stopFullAddress(stop, applications, lw, uw, 'unloading');
              return (
                <div
                  key={String(stop.id ?? `u-${idx}`)}
                  style={{ fontSize: 12, marginBottom: idx < unloadingStops.length - 1 ? 12 : 0 }}
                >
                  {unloadingStops.length > 1 ? (
                    <Text type="secondary" style={{ display: 'block' }}>Точка {idx + 1}</Text>
                  ) : null}
                  <div style={{ marginTop: unloadingStops.length > 1 ? 4 : 0 }}>
                    <Text type="secondary">Название: </Text>
                    <Text>{name}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Город: </Text>
                    <Text>{city}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Адрес: </Text>
                    <Text>{fullAddress}</Text>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {(model.linkedApplicationsSummary.length > 0 || model.carrierContractRequest.number) && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          {model.linkedApplicationsSummary.length > 0 && (
            <div style={{ marginBottom: model.carrierContractRequest.number ? 8 : 0 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Заявки:</Text>
              {model.linkedApplicationsSummary.map((a) => {
                const label = a.internalNumber?.trim()
                  ? `Заявка №${a.internalNumber.trim()}`
                  : `Заявка №${a.applicationId}`;
                return (
                  <div key={a.applicationId} style={{ fontSize: 12, marginTop: 2 }}>
                    <Link to={`/applications/${a.applicationId}`}>{label}</Link>
                    {a.applicationDate ? ` от ${dayjs(a.applicationDate).format('DD.MM.YYYY')}` : ''}
                  </div>
                );
              })}
            </div>
          )}
          {model.carrierContractRequest.number && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Договор-заявка перевозчика:</Text>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                {model.carrierContractRequest.number}
                {model.carrierContractRequest.date
                  ? ` от ${dayjs(model.carrierContractRequest.date).format('DD.MM.YYYY')}`
                  : ''}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default OperationBlock;
